import { Button, Text } from '@lune-fe/lune-ui-lib'
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'
import Box from '@mui/material/Box'
import ImageWithColors from '@public/images/heros/ecotransit-desktop.png'
import ImageWithoutColors from '@public/images/heros/ecotransit-desktop-illustration.png'
import { motion, useSpring, useTransform } from 'framer-motion'
import Image from 'next/image'
import { FC, useEffect, useState } from 'react'

import { spaceGrotesk } from '@/app/fonts'

const HeaderContent: FC<{ title: string; description: string }> = ({ title, description }) => {
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'absolute',
                top: 0,
                left: 0,
            }}
        >
            <Box
                sx={{
                    pb: 3,
                    pt: 9,
                    width: {
                        xs: '100%',
                        md: '65%',
                        lg: '50%',
                    },
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: {
                        xs: 'space-between',
                        md: 'center',
                    },
                    flexDirection: 'column',
                    gap: {
                        xs: 4,
                        md: 5,
                    },
                    maxWidth: {
                        md: '50%',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                        gap: {
                            xs: 4,
                            md: 5,
                        },
                    }}
                >
                    <Text variant={'button'}>EcoTransIT vs. Lune</Text>
                    <Text variant={'h3'}>{title}</Text>
                    <Text variant={'body1'}>{description}</Text>
                </Box>
                <Button
                    sx={{
                        width: {
                            xs: '100%',
                            md: 'unset',
                        },
                    }}
                    variant={'contained'}
                    leftIcon={<ArrowCircleRightIcon />}
                    href={'/request-a-demo'}
                >
                    Upgrade now
                </Button>
            </Box>
        </Box>
    )
}

const MotionChip: FC<{
    initialLabel: string
    movedLabel: string
    positionTop: number
    initialPosition: number
    movedToPosition: number
    animate: boolean
    alwaysBlurry?: boolean
}> = ({
    initialLabel,
    movedLabel,
    positionTop,
    initialPosition,
    movedToPosition,
    animate,
    alwaysBlurry,
}) => {
    const rightValue = useSpring(initialPosition, {
        stiffness: 300,
        damping: 20,
        mass: 1,
    })

    useEffect(() => {
        rightValue.set(animate ? movedToPosition : initialPosition)
    }, [animate, rightValue, movedToPosition, initialPosition])

    const rightPosition = useTransform(rightValue, (value) => `${value}%`)

    return (
        <motion.div
            style={{
                backgroundColor:
                    animate || alwaysBlurry ? 'rgba(255, 255, 255, .15)' : 'rgba(255, 255, 255, 1)',
                padding: '12px 16px',
                borderRadius: '40px',
                border: '1px solid',
                position: 'absolute',
                top: `${positionTop}%`,
                backdropFilter: animate || alwaysBlurry ? 'blur(5px)' : 'blur(0)',
                borderColor: animate || alwaysBlurry ? 'white' : 'rgba(158, 158, 158, 1)',
                right: rightPosition,
            }}
        >
            <Text
                variant={'body3'}
                sx={{
                    color: animate || alwaysBlurry ? 'white' : 'rgba(158, 158, 158, 1)',
                    fontWeight: 700,
                    fontFamily: spaceGrotesk.style.fontFamily,
                }}
            >
                {animate ? movedLabel : initialLabel}
            </Text>
        </motion.div>
    )
}

const EcoTransItVsLuneHero: FC<{ title: string; description: string }> = ({
    title,
    description,
}) => {
    const moveMargin = -15
    const [animate, setAnimate] = useState(false)

    const polygonValue = useSpring(0, {
        stiffness: 300,
        damping: 20,
        mass: 1,
    })

    const clipPath = useTransform(
        polygonValue,
        (value) => `polygon(0 0, ${85 + value}% 0, ${72 + value}% 100%, 0% 100%)`,
    )

    useEffect(() => {
        const handleScroll = () => {
            polygonValue.set(window.scrollY > 100 ? moveMargin : 0)
            setAnimate(window.scrollY > 100)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [polygonValue])

    return (
        <Box
            sx={{
                pb: 0,
                height: {
                    xs: 'auto',
                    md: '792px',
                },
                mt: '-72px !important',
                position: 'relative',

                '.background-image': {
                    height: '100%',
                    width: 'auto',
                    position: 'absolute',
                    left: '-100%',
                    right: '-100%',
                    margin: 'auto',
                    top: 0,
                    zIndex: '-1',
                },
            }}
        >
            <Image
                className={'background-image'}
                width={4000}
                height={1584}
                src={ImageWithColors}
                alt={'page-hero-desktop'}
            />
            <motion.div
                style={{
                    backgroundColor: 'white',
                    clipPath,
                    height: '100%',
                    width: '130%',
                    marginLeft: '-30%',
                }}
            >
                <Image
                    className={'background-image'}
                    width={4000}
                    height={1584}
                    src={ImageWithoutColors}
                    alt={'page-hero-desktop'}
                />
            </motion.div>
            <MotionChip
                initialLabel={'Unknown road type'}
                movedLabel={'Hilly road'}
                initialPosition={32}
                movedToPosition={10}
                positionTop={30}
                animate={animate}
            />
            <MotionChip
                initialLabel={'Generic HGV'}
                movedLabel={'Hybrid Truck'}
                initialPosition={38}
                movedToPosition={33}
                positionTop={52}
                animate={animate}
            />
            <MotionChip
                initialLabel={'Generic diesel'}
                movedLabel={'50% biofuel blend'}
                initialPosition={34}
                movedToPosition={33}
                positionTop={60}
                animate={animate}
            />
            <MotionChip
                initialLabel={'Hamburg → Bremen'}
                movedLabel={'Hamburg → Bremen'}
                alwaysBlurry
                initialPosition={5}
                movedToPosition={0}
                positionTop={48}
                animate={animate}
            />
            <MotionChip
                initialLabel={'Unknown traffic conditions'}
                movedLabel={'No traffic'}
                initialPosition={40}
                movedToPosition={4}
                positionTop={80}
                animate={animate}
            />
            <Box
                onMouseEnter={() => {
                    polygonValue.set(moveMargin)
                    setAnimate(true)
                }}
                onMouseLeave={() => {
                    polygonValue.set(0)
                    setAnimate(false)
                }}
                sx={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    position: 'absolute',
                    right: '-50%',
                    height: '100%',
                    top: 0,
                    zIndex: 1,
                }}
            />
            <HeaderContent title={title} description={description} />
        </Box>
    )
}

export default EcoTransItVsLuneHero
